import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Building2, Mail, Phone, User } from 'lucide-react';
import { saveDemoRequest } from '../mock';

export const RequestDemo = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    complexName: '',
    unitCount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      saveDemoRequest(formData);
      toast({
        title: "Demo Request Berhasil!",
        description: "Tim kami akan menghubungi Anda dalam 1x24 jam.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        complexName: '',
        unitCount: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting demo request:', error);
      toast({
        title: "Gagal Mengirim Request",
        description: "Terjadi kesalahan. Silakan coba lagi.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="demo" className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-6">
            Request <span className="text-brand-700">Demo Gratis</span>
          </h2>
          <p className="text-lg text-foreground/70">
            Isi form di bawah dan tim kami akan menghubungi Anda untuk demo lengkap
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="max-w-2xl mx-auto"
        >
          <form onSubmit={handleSubmit} className="bg-gradient-to-br from-brand-50 to-background rounded-3xl p-8 md:p-12 border border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">
                  Nama Lengkap *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground/40" size={20} />
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="pl-10 h-12 bg-card border-border focus:border-brand-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground/40" size={20} />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="pl-10 h-12 bg-card border-border focus:border-brand-500"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">
                  No. Telepon *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground/40" size={20} />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="08123456789"
                    className="pl-10 h-12 bg-card border-border focus:border-brand-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">
                  Nama Kompleks *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground/40" size={20} />
                  <Input
                    type="text"
                    name="complexName"
                    value={formData.complexName}
                    onChange={handleChange}
                    required
                    placeholder="Kompleks ABC"
                    className="pl-10 h-12 bg-card border-border focus:border-brand-500"
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-foreground/80 mb-2">
                Jumlah Unit
              </label>
              <Input
                type="number"
                name="unitCount"
                value={formData.unitCount}
                onChange={handleChange}
                placeholder="100"
                className="h-12 bg-card border-border focus:border-brand-500"
              />
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-foreground/80 mb-2">
                Pesan Tambahan
              </label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Ceritakan kebutuhan spesifik kompleks Anda..."
                rows={4}
                className="bg-card border-border focus:border-brand-500 resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-brand-700 hover:bg-brand-800 text-white py-6 text-lg font-semibold"
            >
              {isSubmitting ? 'Mengirim...' : 'Request Demo Sekarang'}
            </Button>

            <p className="text-sm text-foreground/60 text-center mt-4">
              Dengan mengirim form ini, Anda setuju dengan kebijakan privasi kami
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
